import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"

import {
  useGetCompanyCustomReportQuery,
  useCreateCompanyCustomReportMutation,
  useUpdateCompanyCustomReportMutation,
  type ReportChartType,
} from "~/ui-rtk/api/companyCustomReportApi"
import MetricExplorerPicker, {
  type TMetricSelection,
} from "./components/MetricExplorerPicker"
import { cubeOf } from "./utils/derive-report"
import { Button, Input, SegmentedToggle } from "~/ui-rtk/components/ui/controls"
import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { H1, H3 } from "~/ui-rtk/components/ui/typography"
import { brandRoutes } from "~/ui-rtk/constants/routes"

type EditorSection = {
  key: string
  name: string
  selection: TMetricSelection
  chartType: ReportChartType
  /**
   * Query keys this editor does not author (`dimensions`, `filters`, `order`,
   * …), carried through load and save untouched. The ported Daily Metrics
   * report's ranked sections are built from exactly these; dropping them on save
   * would turn two filtered top-5 rankings into unfiltered totals rows without
   * telling anyone.
   */
  extraQuery: Record<string, unknown>
}

const CHART_TYPE_OPTIONS = [
  { label: "Bar", value: "bar", title: "Grouped bars" },
  { label: "Line", value: "line", title: "Trend lines" },
  { label: "Stacked", value: "stack", title: "Stacked bars (time trend)" },
]

export default function CustomReportEditorPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const { data: existing, isLoading } = useGetCompanyCustomReportQuery(
    id ?? "",
    {
      skip: !id,
    },
  )
  const [createReport, { isLoading: creating }] =
    useCreateCompanyCustomReportMutation()
  const [updateReport, { isLoading: updating }] =
    useUpdateCompanyCustomReportMutation()

  const [name, setName] = useState("")
  const [sections, setSections] = useState<EditorSection[]>([])
  const [hydrated, setHydrated] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (existing && !hydrated) {
      setName(existing.name)
      setSections(
        [...(existing.reports?.reports ?? [])]
          .sort((a, b) => a.order - b.order)
          .map((s, i) => {
            const { measures, timeDimensions, ...extraQuery } = (s.query ??
              {}) as Record<string, unknown>
            return {
              key: `s-${i}`,
              name: s.name,
              chartType: s.chartType ?? "bar",
              extraQuery,
              selection: {
                // Group-by removed: the picker is measures-only, so `dimensions`
                // rides along in `extraQuery` instead of being authored here.
                measures: (measures as string[]) ?? [],
                dimensions: [],
                timeDimension: (
                  timeDimensions as { dimension?: string }[] | undefined
                )?.[0]?.dimension,
              },
            }
          }),
      )
      setHydrated(true)
    }
  }, [existing, hydrated])

  const addSection = () =>
    setSections(prev => [
      ...prev,
      {
        key: `s-${Date.now()}-${prev.length}`,
        name: `Section ${prev.length + 1}`,
        chartType: "bar",
        selection: { measures: [], dimensions: [] },
        extraQuery: {},
      },
    ])

  const patchSection = (key: string, patch: Partial<EditorSection>) =>
    setSections(prev => prev.map(s => (s.key === key ? { ...s, ...patch } : s)))

  const removeSection = (key: string) =>
    setSections(prev => prev.filter(s => s.key !== key))

  const moveSection = (idx: number, dir: -1 | 1) =>
    setSections(prev => {
      const next = [...prev]
      const j = idx + dir
      if (j < 0 || j >= next.length) return prev
      ;[next[idx], next[j]] = [next[j], next[idx]]
      return next
    })

  // A section must draw from a single cube — Cube can only join cubes that
  // declare joins, so a cross-cube section query would fail at render. The
  // preserved keys count: a ported ranked section carries a group-by and filters
  // the picker never shows, so swapping its measures to another cube would
  // otherwise save a query Cube cannot answer.
  const membersOf = (s: EditorSection) => [
    ...s.selection.measures,
    ...s.selection.dimensions,
    ...((s.extraQuery.dimensions as string[] | undefined) ?? []),
    ...((s.extraQuery.filters as { member?: string }[] | undefined) ?? [])
      .map(f => f.member)
      .filter((member): member is string => typeof member === "string"),
  ]

  const isSingleCube = (s: EditorSection) =>
    new Set(membersOf(s).map(cubeOf)).size <= 1

  const canSave =
    name.trim().length > 0 &&
    sections.length > 0 &&
    sections.every(
      s => s.name.trim() && s.selection.measures.length > 0 && isSingleCube(s),
    )

  const handleSave = async () => {
    setSaveError(null)
    const body = {
      name: name.trim(),
      reports: {
        reports: sections.map((s, i) => ({
          name: s.name.trim(),
          chartType: s.chartType,
          query: {
            dimensions: s.selection.dimensions,
            // Keys the editor never authored (a ranked section's dimensions and
            // filters) win over that default — they are written back exactly as
            // they were loaded.
            ...s.extraQuery,
            measures: s.selection.measures,
            // Self-describing: store the cube's time dimension (or [] when it has
            // none) so the viewer knows whether/how to apply the global date range.
            timeDimensions: s.selection.timeDimension
              ? [{ dimension: s.selection.timeDimension }]
              : [],
          },
          order: i + 1,
        })),
      },
    }
    try {
      if (isEdit && id) {
        await updateReport({ id, body }).unwrap()
        navigate(brandRoutes.CUSTOM_REPORTS.VIEW.replace(":id", id))
      } else {
        const created = await createReport(body).unwrap()
        navigate(brandRoutes.CUSTOM_REPORTS.VIEW.replace(":id", created.id))
      }
    } catch {
      setSaveError("Failed to save the report. Please try again.")
    }
  }

  if (isEdit && isLoading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader size={50} />
      </div>
    )
  }

  return (
    <div className="custom-report-editor px-system-md pb-system-2xl">
      <div className="mt-6 flex items-center justify-between gap-system-sm">
        <H1 className="text-white font-bold text-2xl">
          {isEdit ? "Edit Custom Report" : "New Custom Report"}
        </H1>
        <div className="flex items-center gap-system-sm">
          <Button
            size="sm"
            variant={{ variant: "outlined" }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant={{ variant: "solid", color: "pink" }}
            onClick={handleSave}
            disabled={!canSave || creating || updating}
          >
            {creating || updating ? "Saving…" : "Save report"}
          </Button>
        </div>
      </div>

      {saveError && (
        <div className="mt-4 p-3 bg-error/10 border border-error rounded-lg text-error text-sm">
          {saveError}
        </div>
      )}

      {/* Report name */}
      <div className="mt-system-lg max-w-lg">
        <label className="text-sm text-system-gray-300 mb-1 block">
          Report name
        </label>
        <Input
          value={name}
          placeholder="e.g. Weekly Creative Review"
          onChange={e => setName(e.target.value)}
        />
      </div>

      {/* Sections */}
      <div className="mt-system-xl flex flex-col gap-system-lg">
        {sections.map((section, idx) => (
          <Card
            key={section.key}
            rounded
            className="p-system-md bg-basic-dark-blue border border-basic-blue"
          >
            <div className="flex items-center justify-between gap-system-sm mb-system-sm">
              <H3 className="text-white font-semibold">Section {idx + 1}</H3>
              <div className="flex items-center gap-1">
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  onClick={() => moveSection(idx, -1)}
                  disabled={idx === 0}
                >
                  <ArrowUpIcon className="w-4 h-4" />
                </Button>
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  onClick={() => moveSection(idx, 1)}
                  disabled={idx === sections.length - 1}
                >
                  <ArrowDownIcon className="w-4 h-4" />
                </Button>
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  onClick={() => removeSection(section.key)}
                >
                  <TrashIcon className="w-4 h-4 text-error" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-system-md mb-system-sm">
              <div className="max-w-lg flex-1 min-w-[16rem]">
                <label className="text-sm text-system-gray-300 mb-1 block">
                  Section name
                </label>
                <Input
                  value={section.name}
                  placeholder="Section name"
                  onChange={e =>
                    patchSection(section.key, { name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-sm text-system-gray-300 mb-1 block">
                  Chart type
                </label>
                <SegmentedToggle
                  options={CHART_TYPE_OPTIONS}
                  value={section.chartType}
                  onChange={value =>
                    patchSection(section.key, {
                      chartType: value as ReportChartType,
                    })
                  }
                />
              </div>
            </div>

            <MetricExplorerPicker
              value={section.selection}
              onChange={selection => patchSection(section.key, { selection })}
            />

            {/* The picker warns about ITS OWN cross-cube selection; this covers
                the clash it cannot see — a preserved group-by/filters from a
                different cube than the measures now chosen. */}
            {!isSingleCube(section) &&
              new Set(section.selection.measures.map(cubeOf)).size <= 1 && (
                <div className="text-warning text-sm mt-system-sm">
                  This section keeps a group-by and filters from a different
                  cube than the measures picked above — Cube can&apos;t query
                  the two together. Pick measures from the original cube, or
                  delete this section and add a fresh one.
                </div>
              )}
          </Card>
        ))}
      </div>

      <div className="mt-system-lg">
        <Button
          size="sm"
          variant={{ variant: "outlined" }}
          onClick={addSection}
        >
          <span className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            New section
          </span>
        </Button>
      </div>
    </div>
  )
}
